import { useCallback, useMemo } from 'react';
import { ShippingOptionType } from '../bff-api/baskets/model';
import { useAddProductToBag } from './useAddProductToBag';
import { useBopisStore } from './useBopisStore';
import { useSDDStore } from './useSDDStore';
import type { Product } from '../bff-api/products/model/Product';
import type { Store } from '../bff-api/stores/model';
import type { PDPLocationState } from '../pages/pdp/types';
import type { GTMSourceEvent } from './gtm/model/events/GTMSourceEvent';

export interface UseQuickAddToBagResult {
    handleQuickAdd: (buildProduct: () => Product | undefined) => Promise<void>;
    isLoading: boolean;
    pdpLocationState: PDPLocationState | undefined;
}

interface QuickAddToBagParams {
    shippingOptionType?: ShippingOptionType | null;
    source: GTMSourceEvent;
}

export type UseQuickAddToBag = (params: QuickAddToBagParams) => UseQuickAddToBagResult;

export const useQuickAddToBag: UseQuickAddToBag = ({
    shippingOptionType,
    source,
}: QuickAddToBagParams): UseQuickAddToBagResult => {
    const addProductToBag = useAddProductToBag({
        ignoreInventory: true,
        openDrawer: true,
    });

    const { data: bopisStore } = useBopisStore();
    const { data: sddStore } = useSDDStore();

    const { addToBagShippingProps, pdpLocationState } = useMemo(() => {
        let store: Store | undefined | null;
        let normalizedShippingOptionType = shippingOptionType ?? ShippingOptionType.SHIP_TO;

        if (shippingOptionType === ShippingOptionType.BOPIS) {
            store = bopisStore;
        } else if (shippingOptionType === ShippingOptionType.SDD) {
            store = sddStore;
        }

        if (shippingOptionType !== ShippingOptionType.SHIP_TO && !store) {
            normalizedShippingOptionType = ShippingOptionType.SHIP_TO;
        }

        const shippingProps = {
            shippingOptionType: normalizedShippingOptionType,
            ...(store ? { store } : {}),
        };

        const pdpState =
            (shippingOptionType === ShippingOptionType.BOPIS ||
                shippingOptionType === ShippingOptionType.SDD) &&
            store?.id
                ? ({ shippingOptionType, storeId: store.id } satisfies PDPLocationState)
                : undefined;

        return {
            addToBagShippingProps: shippingProps,
            pdpLocationState: pdpState,
        };
    }, [bopisStore, sddStore, shippingOptionType]);

    const handleQuickAdd = useCallback(
        async (buildProduct: () => Product | undefined) => {
            const product = buildProduct();

            if (!product) {
                return;
            }

            await addProductToBag.mutateAsync({
                ...addToBagShippingProps,
                source,
                product,
                quantity: 1,
            });
        },
        [addToBagShippingProps, addProductToBag, source],
    );

    return {
        handleQuickAdd,
        isLoading: addProductToBag.isLoading,
        pdpLocationState,
    };
};
